import DeleteIcon from "@assets/icons/delete.svg";
import DuplicateIcon from "@assets/icons/duplicate.svg";
import EyeVisibleIcon from "@assets/icons/eye-visible.svg";
import FilterIcon from "@assets/icons/filter.svg";
import { useI18n } from "@core/i18n/i18n";
import { libraryFilterManager, onboardingManager } from "@core/service-container";
import { Exercise, exerciseCategories, MainExercise } from "@domain/exercise/exercise";
import {
	AnalyticsViewExerciseDetailOrigin,
	sendAnalyticsEventDuplicateExercise,
	sendAnalyticsEventOpenComplementaryExercises,
	sendAnalyticsEventRemoveExercise,
} from "@domain/library/exercise-analytics";
import { useExerciseFormatter } from "@ui/components/exercise/hooks/use-exercise-formatter";
import { OnboardingProgramQuickstartStep } from "@domain/onboarding/onboarding";
import { useUser } from "@domain/user/hooks/use-user";
import styled from "@emotion/styled";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { MenuButton, MenuPosition } from "@ui/components/common/menu-button";
import { TrackingContext } from "@ui/components/common/tracking-context-provider";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { ExerciseDragAndDropContext } from "@ui/pages/program-builder/drag-drop/program-builder-drag-drop-context";
import { useEditorExerciseCardActions } from "@ui/pages/program-builder/hooks/use-editor-exercise-card-actions";
import {
	createBodyPartsFilter,
	createCategoriesFilter,
} from "@domain/exercise/exercise-filters";
import { ExerciseLibraryFlow } from "@ui/pages/program-builder/mobile/dialog/mobile-exercise-library-dialog";
import { complementaryTooltipVisible, complementaryQuickstartOrigin, clearComplementaryQuickstartOrigin, showComplementaryGuide3, hideComplementaryTooltip, resetComplementaryGuide3 } from "@ui/pages/program-builder/quickstart/complementary-tooltip-signal";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";

import React, { useCallback, useContext, useMemo } from "react";
import { useObservable, observable } from "micro-observables";

interface Props {
	exercise: MainExercise;
	onlyAllowPreview?: boolean;
	shouldDisplayHalo?: boolean;
}

export const ExerciseOptions: React.FC<Props> = ({ exercise, onlyAllowPreview = false, shouldDisplayHalo = false }) => {
	const { format } = useI18n();
	const user = useUser();
	const exerciseDragAndDropContext = useContext(ExerciseDragAndDropContext);
	const dialogContext = useContext(DialogContext);
	const trackingContext = useContext(TrackingContext);
	const { isDesktop } = useDimensions();

	// Halo visibility for the menu item: only on the first exercise and while Quickstart flow is active.
	// Use the persistent origin flag so it remains highlighted even if the tooltip hides on menu open.
	const fallbackFirstExerciseId$ = useMemo(
		() => observable<string | undefined>(undefined),
		[],
	);
	const firstExerciseIdObservable = useObservable(
		exerciseDragAndDropContext?.programEditor.firstExerciseDraggableId ?? fallbackFirstExerciseId$,
	);
	const firstExerciseId = exerciseDragAndDropContext ? firstExerciseIdObservable : undefined;
	const isComplementaryTooltipVisible = useObservable(complementaryTooltipVisible);
	const isComplementaryOrigin = useObservable(complementaryQuickstartOrigin);
	const isQuickstartComplementaryActive = firstExerciseId === exercise.draggableId && (isComplementaryTooltipVisible || isComplementaryOrigin);

	const { openExerciseDetailsModal } = useEditorExerciseCardActions();
	const exerciseIndices = useMemo(
		() => exerciseDragAndDropContext?.programEditor.getExerciseIndex(exercise.draggableId),
		[exercise.draggableId, exerciseDragAndDropContext?.programEditor],
	);

	const relatedExercises = useMemo(
		() =>
			exerciseIndices && exerciseDragAndDropContext
				? exerciseDragAndDropContext.programEditor.getDaySessions(exerciseIndices.sessionIndex)
				: [],
		[exerciseDragAndDropContext, exerciseIndices],
	);

	const { formatExerciseBodyPart, formatExerciseCategory } = useExerciseFormatter();

	const viewExercise = useCallback(() => {
		isNotNil(user) &&
			openExerciseDetailsModal(exercise, relatedExercises, AnalyticsViewExerciseDetailOrigin.CANVAS_EXERCISE_DETAIL);
	}, [exercise, openExerciseDetailsModal, relatedExercises, user]);

	const filterComplementaryExercises = useCallback(
		(selectedExercise: Exercise) => {
			const filteredBodyParts = [selectedExercise.primaryBodyPart, ...selectedExercise.secondaryBodyParts];
			const filteredCategories = exerciseCategories.filter(category => selectedExercise.category !== category);
			libraryFilterManager.activeCategoryFilters.set([
				createCategoriesFilter(filteredCategories, format("exercise.category.title"), formatExerciseCategory),
				createBodyPartsFilter(filteredBodyParts, format("exercise.bodyPart.title"), formatExerciseBodyPart),
			]);
		},
		[format, formatExerciseBodyPart, formatExerciseCategory],
	);

	const duplicateExercise = useCallback(() => {
		if (exerciseIndices && exerciseDragAndDropContext) {
			exerciseDragAndDropContext.programEditor.duplicateExercise(
				exerciseIndices.sessionIndex,
				exerciseIndices.exerciseIndex,
			);
			sendAnalyticsEventDuplicateExercise(exercise, trackingContext.category);
		}
	}, [exercise, exerciseDragAndDropContext, exerciseIndices, trackingContext.category]);

	const deleteExercise = useCallback(() => {
		if (exerciseIndices && exerciseDragAndDropContext) {
			exerciseDragAndDropContext.programEditor.deleteExercise(
				exerciseIndices.sessionIndex,
				exerciseIndices.exerciseIndex,
			);
			sendAnalyticsEventRemoveExercise(exercise, trackingContext.category);
		}
	}, [exercise, exerciseDragAndDropContext, exerciseIndices, trackingContext.category]);

	const handleMobileComplementaryClose = useCallback(
			(exercises: Exercise[]) => {
				const newExercises = exercises as MainExercise[];
				if (isNotNil(exerciseIndices)) {
					for (const newExercise of newExercises) {
						exerciseDragAndDropContext?.programEditor.addExerciseToSession(
							newExercise,
							exerciseIndices.sessionIndex,
							exerciseDragAndDropContext.programEditor.getExerciseCountInSession(
								exerciseIndices.sessionIndex,
							),
							true,
						);
					}
				}
			},
			[exerciseDragAndDropContext, exerciseIndices],
		);

	const handleComplementaryClick = useCallback(async () => {
		// Close the click-driven Quickstart tooltip as soon as the user commits to this action
		hideComplementaryTooltip();
		resetComplementaryGuide3();
		libraryFilterManager.clearLastAddedExerciseMetadata();
		await libraryFilterManager.clearLibraryFiltersAndSearch();
		filterComplementaryExercises(exercise);
		libraryFilterManager.setIsComplementaryExerciseActive();
		sendAnalyticsEventOpenComplementaryExercises(exercise);
		if (complementaryQuickstartOrigin.get()) {
			await onboardingManager.passStepOnboarding(
				OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_02,
			);
			showComplementaryGuide3();
			clearComplementaryQuickstartOrigin();
		} else {
			void onboardingManager.passStepOnboarding(
				OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_02,
			);
		}
		if (isDesktop) {
			libraryFilterManager.setSidebarVisible(true);
			return;
		}
		dialogContext?.openModal(ModalId.MobileExerciseLibrary, {
			exerciseLibraryAddingFlow: ExerciseLibraryFlow.ComplementaryExercises,
			onClose: handleMobileComplementaryClose,
		});
	}, [dialogContext, exercise, filterComplementaryExercises, isDesktop, handleMobileComplementaryClose]);

	const buildPreviewMenuItems = useCallback(
		() => [
			{
				text: format("programBuilder.exerciseCard.details"),
				icon: <EyeVisibleIcon />,
				onClick: viewExercise,
			},
		],
		[format, viewExercise],
	);

	const buildFullMenuItems = useCallback(
		() => [
			{
				text: format("programBuilder.exerciseCard.details"),
				icon: <EyeVisibleIcon />,
				onClick: viewExercise,
			},
			{
				text: format("programBuilder.exerciseCard.complementaryExercises"),
				icon: <FilterIcon />,
				shouldDisplayHaloItem: isQuickstartComplementaryActive,
				onClick: () => { handleComplementaryClick(); },
			},
			{
				text: format("programBuilder.exerciseCard.duplicate"),
				icon: <DuplicateIcon />,
				onClick: duplicateExercise,
			},
			{
				text: format("programBuilder.exerciseCard.delete"),
				icon: <DeleteIcon />,
				onClick: deleteExercise,
				destructive: true,
			},
		],
		[
			deleteExercise,
			duplicateExercise,
			format,
			isQuickstartComplementaryActive,
			viewExercise,
			handleComplementaryClick,
		],
	);

	const items = useMemo(
		() => (onlyAllowPreview ? buildPreviewMenuItems() : buildFullMenuItems()),
		[onlyAllowPreview, buildPreviewMenuItems, buildFullMenuItems],
	);

	return (
		<StyledMenuButton
			menuItems={items}
			menuId={`popup_menu_${exercise.draggableId}`}
			menuPosition={MenuPosition.CenteredPosition}
			menuIconSize={24}
			shouldDisplayHalo={shouldDisplayHalo}
			onMenuClick={() => {
				void onboardingManager.passStepOnboarding(
					OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_01,
				);
				// Hide the tooltip after the menu has toggled to avoid UI interference
				setTimeout(() => hideComplementaryTooltip(), 0);
			}}
		/>
	);
};

const StyledMenuButton = styled(MenuButton)`
	> button {
		padding: 0 2px;

		> div {
			background-color: unset;
			width: auto;
			height: auto;
			padding: 6px;

			:hover {
				background-color: ${colors.primaryBackground};
			}
		}
	}
`;
